import type { Ref } from 'vue'
import { useTheme } from 'vuetify'
import { useConfigStore } from '@core/stores/config'
import { cookieRef } from '@layouts/stores/config'
import { $api } from '@/utils/api'
import { useCookie } from '@core/composable/useCookie'
import { usePlatformBranding } from '@/composables/usePlatformBranding'

interface ThemePreferences {
  theme?: string
  skin?: string
  isVerticalNavSemiDark?: boolean
  isVerticalNavCollapsed?: boolean
  appContentWidth?: string
  appContentLayoutNav?: string
  isAppRTL?: boolean
  lightThemePrimaryColor?: string | null
  darkThemePrimaryColor?: string | null
  lightThemePrimaryDarkenColor?: string | null
  darkThemePrimaryDarkenColor?: string | null
}

/**
 * Sincronizza le impostazioni del Theme Customizer (finora solo nei
 * cookie del browser, vedi @core/stores/config.ts e @layouts/stores/config.ts)
 * con il DB per-utente, cosi' si ritrovano identiche da qualunque
 * browser/dispositivo con lo stesso account. I cookie restano la fonte
 * immediata per il primo render (niente flash del tema sbagliato); il
 * fetch dal server, quando risolve, applica le preferenze salvate sopra
 * ai valori locali.
 *
 * @param isWhitelabel Un tenant whitelabel ha il colore primario sempre
 *   forzato al proprio brand (vedi il watch dedicato in App.vue, che vince
 *   sempre) - il colore scelto nel customizer e' gia' oggi ininfluente in
 *   quel caso, quindi qui viene escluso dalla sincronizzazione per non
 *   salvare/riapplicare un colore che l'utente non ha davvero scelto.
 */
export const useThemePreferencesSync = (isWhitelabel: Ref<boolean> = ref(false)) => {
  const configStore = useConfigStore()
  const vuetifyTheme = useTheme()

  // Confronto per contenuto (non un flag temporale): evita che il watcher
  // qui sotto rispedisca al server, in un giro inutile, esattamente i
  // valori appena ricevuti da fetchFromServer().
  let lastKnownServerValue: string | null = null

  const isLoggedIn = () => {
    try {
      return !!(useCookie('userData').value && useCookie('accessToken').value)
    } catch {
      return false
    }
  }

  const buildSnapshot = (): ThemePreferences => ({
    theme: configStore.theme,
    skin: configStore.skin,
    isVerticalNavSemiDark: configStore.isVerticalNavSemiDark,
    isVerticalNavCollapsed: configStore.isVerticalNavCollapsed,
    appContentWidth: configStore.appContentWidth,
    appContentLayoutNav: configStore.appContentLayoutNav,
    isAppRTL: configStore.isAppRTL,
    ...(isWhitelabel.value
      ? {}
      : {
          lightThemePrimaryColor: cookieRef<string | null>('lightThemePrimaryColor', null).value,
          darkThemePrimaryColor: cookieRef<string | null>('darkThemePrimaryColor', null).value,
          lightThemePrimaryDarkenColor: cookieRef<string | null>('lightThemePrimaryDarkenColor', null).value,
          darkThemePrimaryDarkenColor: cookieRef<string | null>('darkThemePrimaryDarkenColor', null).value,
        }),
  })

  const applyPreferences = (prefs: ThemePreferences) => {
    if (prefs.theme)
      configStore.theme = prefs.theme as any
    if (prefs.skin)
      configStore.skin = prefs.skin as any
    if (typeof prefs.isVerticalNavSemiDark === 'boolean')
      configStore.isVerticalNavSemiDark = prefs.isVerticalNavSemiDark
    if (typeof prefs.isVerticalNavCollapsed === 'boolean')
      configStore.isVerticalNavCollapsed = prefs.isVerticalNavCollapsed
    if (prefs.appContentWidth)
      configStore.appContentWidth = prefs.appContentWidth as any
    if (prefs.appContentLayoutNav)
      configStore.appContentLayoutNav = prefs.appContentLayoutNav as any
    if (typeof prefs.isAppRTL === 'boolean')
      configStore.isAppRTL = prefs.isAppRTL

    if (!isWhitelabel.value) {
      if (prefs.lightThemePrimaryColor) {
        cookieRef<string | null>('lightThemePrimaryColor', null).value = prefs.lightThemePrimaryColor
        vuetifyTheme.themes.value.light.colors.primary = prefs.lightThemePrimaryColor
      }
      if (prefs.lightThemePrimaryDarkenColor) {
        cookieRef<string | null>('lightThemePrimaryDarkenColor', null).value = prefs.lightThemePrimaryDarkenColor
        vuetifyTheme.themes.value.light.colors['primary-darken-1'] = prefs.lightThemePrimaryDarkenColor
      }
      if (prefs.darkThemePrimaryColor) {
        cookieRef<string | null>('darkThemePrimaryColor', null).value = prefs.darkThemePrimaryColor
        vuetifyTheme.themes.value.dark.colors.primary = prefs.darkThemePrimaryColor
      }
      if (prefs.darkThemePrimaryDarkenColor) {
        cookieRef<string | null>('darkThemePrimaryDarkenColor', null).value = prefs.darkThemePrimaryDarkenColor
        vuetifyTheme.themes.value.dark.colors['primary-darken-1'] = prefs.darkThemePrimaryDarkenColor
      }
    }

    lastKnownServerValue = JSON.stringify(buildSnapshot())
  }

  const fetchFromServer = async () => {
    if (!isLoggedIn())
      return

    try {
      const response = await $api<{ theme_preferences: ThemePreferences | null }>('/account/theme-preferences')
      if (response?.theme_preferences) {
        applyPreferences(response.theme_preferences)
        return
      }
    } catch {
      // richiesta fallita: prova comunque il default piattaforma sotto.
    }

    // Nessuna preferenza personale salvata: applica il default impostato
    // dal Platform Admin, se presente (altrimenti restano i valori
    // hardcoded di themeConfig). /public/platform/status e' gia'
    // fetchato altrove in App.vue (usePlatformBranding condivide lo
    // stato tra tutti i chiamanti), quindi questa chiamata e' quasi
    // sempre una cache hit, non una richiesta di rete aggiuntiva.
    try {
      const { platformThemeDefaults, loadPlatformBranding } = usePlatformBranding()
      await loadPlatformBranding()
      if (platformThemeDefaults.value)
        applyPreferences(platformThemeDefaults.value as ThemePreferences)
    } catch {
      // Nessun default piattaforma configurato: nessun impatto visibile.
    }
  }

  /**
   * Salva le impostazioni ATTUALI del customizer come default piattaforma
   * - chiamato solo dal pulsante dedicato nel customizer, visibile solo al
   * Platform Admin (role === 'admin'). Non tocca users.theme_preferences:
   * scrive su global_settings via PlatformGeneralSettings, letto da
   * chiunque non abbia ancora una preferenza personale propria.
   */
  const saveAsPlatformDefault = async () => {
    const snapshot = buildSnapshot()

    await $api('/admin/settings/theme-defaults', {
      method: 'PUT',
      body: { theme_preferences: snapshot },
    })

    return snapshot
  }

  const saveToServer = useDebounceFn(async () => {
    if (!isLoggedIn())
      return

    const snapshot = buildSnapshot()
    const serialized = JSON.stringify(snapshot)
    if (serialized === lastKnownServerValue)
      return

    lastKnownServerValue = serialized

    try {
      await $api('/account/theme-preferences', {
        method: 'PUT',
        body: { theme_preferences: snapshot },
      })
    } catch {
      // Il tema resta comunque applicato localmente (cookie); si ritentera'
      // al prossimo cambiamento.
    }
  }, 600)

  const startWatching = () => {
    watch(
      [
        () => configStore.theme,
        () => configStore.skin,
        () => configStore.isVerticalNavSemiDark,
        () => configStore.isVerticalNavCollapsed,
        () => configStore.appContentWidth,
        () => configStore.appContentLayoutNav,
        () => configStore.isAppRTL,
        () => vuetifyTheme.themes.value.light.colors.primary,
        () => vuetifyTheme.themes.value.dark.colors.primary,
      ],
      () => saveToServer(),
    )
  }

  return { fetchFromServer, startWatching, saveAsPlatformDefault }
}
